import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type DefectMarkType = 'scratch' | 'dent' | 'crack' | 'wear' | 'other'
export type DefectSeverity = 'minor' | 'visible' | 'significant'
export type DefectPart = 'screen' | 'frame' | 'back_glass' | 'camera' | 'other'

export type DefectMark = {
  id?: string
  displayIndex?: number
  x: number
  y: number
  label: string
  photo_id?: string
  photo_index?: number
  part?: DefectPart
  type?: DefectMarkType
  severity?: DefectSeverity
  description?: string
}

type Props = {
  imageUrl: string | null
  marks: DefectMark[]
  onChange: (marks: DefectMark[]) => void
  embedded?: boolean
  /** Global defect number across all photos (defaults to per-photo index). */
  getMarkNumber?: (mark: DefectMark) => number
}

/** Click-to-pin defects on the cover photo (optional sell-form step). */
export default function DefectMarkPad({
  imageUrl,
  marks,
  onChange,
  embedded = false,
  getMarkNumber,
}: Props) {
  const { t } = useTranslation()
  const wrapRef = useRef<HTMLDivElement>(null)
  const [draftLabel, setDraftLabel] = useState('')

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!imageUrl || !wrapRef.current) return
      const rect = wrapRef.current.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return
      const x = Math.round(((e.clientX - rect.left) / rect.width) * 1000) / 10
      const y = Math.round(((e.clientY - rect.top) / rect.height) * 1000) / 10
      const label = draftLabel.trim() || t('listing.defectMarkPin')
      onChange([...marks, { x, y, label }])
      setDraftLabel('')
    },
    [draftLabel, imageUrl, marks, onChange, t],
  )

  function removeAt(index: number) {
    onChange(marks.filter((_, i) => i !== index))
  }

  return (
    <div className={`defect-mark-block${embedded ? ' defect-mark-block--embedded' : ''}`}>
      {!embedded ? (
        <>
          <span className="device-photos-label">{t('listing.defectMarkTitle')}</span>
          <p className="meta-text">{t('listing.defectMarkHint')}</p>
        </>
      ) : null}
      {!imageUrl ? (
        <p className="meta-text">{t('listing.defectMarkNeedPhoto')}</p>
      ) : (
        <>
          <input
            type="text"
            className="defect-mark-label-input"
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            placeholder={t('listing.defectMarkPin')}
            aria-label={t('listing.defectMarkPin')}
          />
          <div
            ref={wrapRef}
            className="defect-mark-canvas"
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') e.preventDefault()
            }}
            aria-label={t('listing.defectMarkEmpty')}
          >
            <img src={imageUrl} alt="" draggable={false} />
            {marks.map((m, i) => (
              <button
                key={`${m.x}-${m.y}-${i}`}
                type="button"
                className="defect-mark-pin"
                style={{ left: `${m.x}%`, top: `${m.y}%` }}
                title={t('listing.defectMarkRemove')}
                aria-label={t('listing.defectMarkRemove')}
                onClick={(e) => {
                  e.stopPropagation()
                  removeAt(i)
                }}
              >
                {getMarkNumber ? getMarkNumber(m) : i + 1}
              </button>
            ))}
          </div>
          {marks.length === 0 ? (
            <p className="meta-text">{t('listing.defectMarkEmpty')}</p>
          ) : (
            <ul className="defect-mark-list">
              {marks.map((m, i) => (
                <li key={`${m.x}-${m.y}-${i}`} className="defect-mark-list__row">
                  <span>
                    {getMarkNumber ? getMarkNumber(m) : i + 1}. {m.label}
                  </span>
                  <button
                    type="button"
                    className="defect-mark-remove-btn"
                    onClick={() => removeAt(i)}
                  >
                    {t('listing.defectMarkRemove')}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {marks.length > 0 ? (
            <button type="button" className="defect-mark-clear" onClick={() => onChange([])}>
              {t('listing.defectMarkClear')}
            </button>
          ) : null}
        </>
      )}
    </div>
  )
}
